import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Organization } from '@models/organization.model';
import { UserLocale, UserRoles } from '@models/user.model';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { RootState } from '@redux/store';
import { SetStateAction, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import WolfButton from '../ui/WolfButton';
import WolfInput from '../ui/WolfInput';
import UserOrganizationsModal from './UserOrganizationsModal';

interface CreateEditUserModalProps {
  modalClose: () => void;
  createUser?: (
    organizationId: string,
    email: string,
    firstName: string,
    lastName: string,
    role: UserRoles,
  ) => void;
  createUserByEmail?: (organizationId: string, email: string, role: UserRoles) => void;
  editUser?: (
    organizationId: string,
    email: string,
    firstName: string,
    lastName: string,
    userId: string,
    role?: UserRoles,
    seatAssignmentId?: string,
    currentRole?: UserRoles,
  ) => void;
  organizations: Organization[];
  saving: boolean;
  editing?: boolean;
  useSimplifiedCreation?: boolean; // Flag to use simplified creation (email only)
  isFirstUser?: boolean; // Flag to indicate this is the first user (forces USER role)
  availableRoles?: UserRoles[]; // Roles available based on seat availability
  isAdminRestricted?: boolean; // Flag to indicate admin role is restricted (no subscription or no admin seats)
  onAdminRestrictedClick?: () => void; // Callback when user tries to select restricted admin role
  availableAdminSeats?: number; // Number of available admin seats
  availableUserSeats?: number; // Number of available user seats
  initialData?:
    | { email: string; firstName: string; lastName: string; userId: string; role: UserRoles; locale: UserLocale; seatAssignmentId?: string }
    | null
    | undefined;
}

const CreateEditUserModal: React.FC<CreateEditUserModalProps> = ({
  modalClose,
  createUser,
  createUserByEmail,
  editUser,
  organizations,
  saving,
  editing = false,
  useSimplifiedCreation = false,
  isFirstUser = false,
  availableRoles,
  isAdminRestricted = false,
  onAdminRestrictedClick,
  availableAdminSeats = 0,
  availableUserSeats = 0,
  initialData,
}) => {
  const localeCommon = useTranslation('common');
  const localeOrganizations = useTranslation('organizations');
  const localeUsers = useTranslation('users');
  const [userEmail, setUserEmail] = useState<string | null>('');
  const [firstName, setFirstName] = useState<string | null>('');
  const [lastName, setLastName] = useState<string | null>('');
  const [organizationId, setOrganizationId] = useState<string | null>(organizations?.[0]?.organizationId || '');
  const [userRole, setUserRole] = useState<UserRoles>(UserRoles.USER);
  const [showOrganizationsModal, setShowOrganizationsModal] = useState(false);

  // Store initial values for dirty tracking
  const [initialValues, setInitialValues] = useState({
    userEmail: '',
    firstName: '',
    lastName: '',
    organizationId: organizations?.[0]?.organizationId || '',
    userRole: UserRoles.USER,
  });

  const dispatch = useDispatch();

  const { role } = useAppSelector((state: RootState) => state.user);

  // Show roles based on current user permissions
  const roles = role === UserRoles.SUPER_ADMIN
    ? [UserRoles.SUPER_ADMIN, UserRoles.OWNER, UserRoles.ADMIN, UserRoles.USER]
    : [UserRoles.ADMIN, UserRoles.USER];

  // Determine if a role should be disabled (grayed out)
  const isRoleDisabled = (roleOption: UserRoles): boolean => {
    // First user must have USER role - disable ADMIN
    if (roleOption === UserRoles.ADMIN && isFirstUser && !editing) {
      return true;
    }
    // Admin is restricted when there's no subscription or no admin seats
    if (roleOption === UserRoles.ADMIN && isAdminRestricted && !editing) {
      return true;
    }
    // Also disable if role is not in availableRoles (when availableRoles is provided and not empty)
    if (!editing && availableRoles && availableRoles.length > 0 && !availableRoles.includes(roleOption)) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (editing && initialData) {
      const values = {
        userEmail: initialData.email,
        firstName: initialData.firstName,
        lastName: initialData.lastName,
        organizationId: organizationId || '',
        userRole: initialData.role,
      };

      setUserEmail(values.userEmail);
      setUserRole(values.userRole);
      setFirstName(values.firstName);
      setLastName(values.lastName);
      setInitialValues(values);
    } else {
      // For creation, set initial values to current defaults
      const values = {
        userEmail: '',
        firstName: '',
        lastName: '',
        organizationId: organizations?.[0]?.organizationId || '',
        userRole: UserRoles.USER,
      };
      setInitialValues(values);
    }
  }, [editing, initialData, organizations, organizationId]);

  // Ensure first user always has USER role
  useEffect(() => {
    if (isFirstUser && !editing) {
      setUserRole(UserRoles.USER);
    }
  }, [isFirstUser, editing]);

  // Auto-select role if only one is available
  useEffect(() => {
    if (!editing && availableRoles && availableRoles.length === 1 && !isFirstUser) {
      setUserRole(availableRoles[0]);
    }
  }, [availableRoles, editing, isFirstUser]);

  // Function to check if form is dirty (has changes)
  const isFormDirty = () => {
    if (!editing) {
      // For creation, form is dirty if any required field has content
      return !!(userEmail && userEmail.trim());
    }

    // For editing, compare current values with initial values
    return (
      userEmail !== initialValues.userEmail ||
      firstName !== initialValues.firstName ||
      lastName !== initialValues.lastName ||
      (organizationId || '') !== initialValues.organizationId ||
      userRole !== initialValues.userRole
    );
  };

  const handleEmailChange = (event: { target: { value: string | null } }) => {
    if (event.target.value) {
      const trimmedValue = event.target.value.trim();
      setUserEmail(trimmedValue);
      event.target.value = trimmedValue;
    } else {
      setUserEmail('');
    }
  };

  const handleFirstNameChange = (event: { target: { value: SetStateAction<string | null> } }) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event: { target: { value: SetStateAction<string | null> } }) => {
    setLastName(event.target.value);
  };

  const handleRoleChange = (event: { target: { value: string } }) => {
    const selectedRole = event.target.value as UserRoles;

    // Prevent non-super admins from selecting OWNER role
    if (selectedRole === UserRoles.OWNER && role !== UserRoles.SUPER_ADMIN) {
      dispatch(
        appendActionMessage({
          message: localeUsers['ownerRoleCannotBeSelected'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    // If admin is restricted and user tries to select it, show paywall
    if (selectedRole === UserRoles.ADMIN && isAdminRestricted && !editing) {
      if (onAdminRestrictedClick) {
        onAdminRestrictedClick();
      }
      return;
    }

    setUserRole(roles.find((r) => r === selectedRole) || UserRoles.USER);
  };

  const submit = () => {
    if (editing && initialData) {
      editUser &&
        editUser(
          '',
          userEmail || '',
          firstName || '',
          lastName || '',
          initialData?.userId || '',
          userRole,
          initialData?.seatAssignmentId,
          initialData?.role
        );
    } else {
      // Check if we should use simplified creation (email only) or full creation
      if (useSimplifiedCreation && createUserByEmail) {
        // Use simplified creation - only email and role
        createUserByEmail(organizationId || '', userEmail || '', userRole);
      } else {
        // Use traditional creation with firstName and lastName
        // The organizationId will only be relevant if the user is a super admin, since other roles can only act in their own tenant
        // The backend will also block attempts by no super-owner roles to create users in other organizations
        createUser &&
          createUser(organizationId || '', userEmail || '', firstName || '', lastName || '', userRole);
      }
    }
    modalClose();
  };

  return (
    <Box>
      {/* Only show firstName and lastName for editing or when not using simplified creation */}
      {(editing || !useSimplifiedCreation) && (
        <>
          <WolfInput
            label={localeCommon['user'] + ' ' + localeUsers['firstName']}
            id="firstName-input"
            placeholder={localeCommon['user'] + ' ' + localeUsers['firstName']}
            name="firstName"
            value={firstName}
            onChange={handleFirstNameChange}
            fullWidth
          />
          <WolfInput
            label={localeCommon['user'] + ' ' + localeUsers['lastName']}
            id="lastName-input"
            placeholder={localeCommon['user'] + ' ' + localeUsers['lastName']}
            name="lastName"
            value={lastName}
            onChange={handleLastNameChange}
            fullWidth
          />
        </>
      )}
      <WolfInput
        label={localeCommon['user'] + ' ' + localeCommon['email']}
        id="email-input"
        placeholder={localeCommon['user'] + ' ' + localeCommon['email']}
        name="email"
        value={userEmail}
        onChange={handleEmailChange}
        fullWidth
      />

      {/* Show role selection when creating a new user or when editing if not super admin
          SUPER_ADMIN manages roles via organization management modal */}
      {(!editing || (role !== UserRoles.SUPER_ADMIN)) && (
        <Box>
          {/* // TODO: Refactor using ui component */}
          <Typography variant="body14semibold">{localeCommon['user'] + ' ' + localeUsers['role']}</Typography>
          <TextField
            id="role-input"
            select
            placeholder={localeCommon['user'] + ' ' + localeUsers['role']}
            name="role"
            className="border rounded border-gray-400"
            value={userRole}
            onChange={handleRoleChange}
            disabled={editing && initialData?.role === UserRoles.OWNER && role !== UserRoles.SUPER_ADMIN}
            fullWidth>
            {roles.map((roleOption) => {
              const disabled = isRoleDisabled(roleOption);
              const isAdminDisabledForUpgrade = roleOption === UserRoles.ADMIN && isAdminRestricted && !editing;
              const isAdminDisabledForFirstUser = roleOption === UserRoles.ADMIN && isFirstUser && !editing;
              const showGrayedOut = isAdminDisabledForUpgrade || isAdminDisabledForFirstUser;

              // Get seat count for this role
              const seatCount = roleOption === UserRoles.ADMIN ? availableAdminSeats :
                               roleOption === UserRoles.USER ? availableUserSeats : null;
              const showSeatCount = !editing && !isFirstUser && seatCount !== null && (availableAdminSeats > 0 || availableUserSeats > 0);

              return (
                <MenuItem
                  key={roleOption}
                  value={roleOption}
                  disabled={disabled}
                  sx={showGrayedOut ? {
                    opacity: 0.5,
                    '&.Mui-disabled': {
                      opacity: 0.5,
                    }
                  } : {}}
                  onClick={(e) => {
                    if (isAdminDisabledForUpgrade && onAdminRestrictedClick) {
                      e.preventDefault();
                      e.stopPropagation();
                      onAdminRestrictedClick();
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{roleOption}</span>
                        {showSeatCount && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontWeight: 400,
                            }}
                          >
                            ({seatCount} {localeUsers['seatsLeft']})
                          </Typography>
                        )}
                      </Box>
                      {isAdminDisabledForUpgrade && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            cursor: 'pointer',
                            ml: 2,
                          }}
                        >
                          {localeUsers['upgradePlan']}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              );
            })}
          </TextField>
          {isFirstUser && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {localeUsers['firstUserRoleUser']}
            </Typography>
          )}
          {editing && initialData?.role === UserRoles.OWNER && role !== UserRoles.SUPER_ADMIN && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {localeUsers.ownerRoleModificationRestriction}
            </Typography>
          )}
          {!editing && availableRoles && availableRoles.length === 1 && availableRoles[0] === UserRoles.ADMIN && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {localeUsers['onlyAdminSeatsAvailableRestriction']}
            </Typography>
          )}
          {!editing && availableRoles && availableRoles.length === 1 && availableRoles[0] === UserRoles.USER && !isAdminRestricted && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {localeUsers['onlyUserSeatsAvailableRestriction']}
            </Typography>
          )}
          {!editing && isAdminRestricted && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {localeUsers['adminRoleRequiresUpgrade']}
              {' '}
              <Typography
                component="span"
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onClick={onAdminRestrictedClick}
              >
                {localeUsers['upgradePlan']}
              </Typography>
            </Typography>
          )}
        </Box>
      )}

      {/* Show Organizations management for SUPER_ADMINs when editing */}
      {editing && role === UserRoles.SUPER_ADMIN && initialData && (
        <Box className="mt-4">
          {/* <Typography variant="body14semibold">Organizations</Typography> */}
          <WolfButton
            onClick={() => setShowOrganizationsModal(true)}
            color="primary"
            variant="outlined"
            fullWidth
            sx={{ mt: 1 }}>
            Manage Organizations
          </WolfButton>
        </Box>
      )}

      {/* We only want to show the organization select if we are not editing, since users are not allowed to change their organization */}
      {/* And we only want to show the organization select if the user is a super admin, since other roles can only act in their own tenant */}
      {!editing && role === UserRoles.SUPER_ADMIN && (
        <Box>
          {/* // TODO: Refactor using ui component */}
          <Typography variant="body14semibold">
            {localeCommon['user'] + ' ' + localeOrganizations['organization']}
          </Typography>
          <TextField
            id="organization-input"
            select
            placeholder={localeCommon['user'] + ' ' + localeOrganizations['organization']}
            name="organization"
            className="border rounded border-gray-400"
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
            fullWidth>
            {organizations.map((organization) => (
              <MenuItem key={organization.organizationId} value={organization.organizationId}>
                {organization.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
      {/* { TODO: Locale is not used temporarily, but we should add it back in the future
        <Box>
          <Typography variant="body14semibold">{localeCommon['user'] + ' ' + localeUsers['locale']}</Typography>
          <TextField
            id="locale-input"
            select
            placeholder={localeUsers['locale']}
            name="locale"
            className="border rounded border-gray-400"
            value={userLocale}
            onChange={(event) => setUserLocale(event.target.value as UserLocale)}
            fullWidth>
            <MenuItem value={UserLocale.EN}>{localeSettings['english']}</MenuItem>
            <MenuItem value={UserLocale.ES}>{localeSettings['spanish']}</MenuItem>
          </TextField>
        </Box>
      } */}
      <Box className="flex justify-between my-4">
        <WolfButton onClick={modalClose} color="secondary" variant="outlined">
          {localeCommon['close']}
        </WolfButton>
        <WolfButton onClick={submit} color="primary" autoFocus disabled={saving || !isFormDirty()} variant="outlined">
          {editing ? localeCommon['save'] : localeCommon['create']}
        </WolfButton>
      </Box>

      {/* User Organizations Modal for SUPER_ADMIN */}
      {editing && role === UserRoles.SUPER_ADMIN && initialData && (
        <UserOrganizationsModal
          isOpen={showOrganizationsModal}
          onClose={() => setShowOrganizationsModal(false)}
          userId={initialData.userId}
          userEmail={initialData.email}
          organizations={organizations}
        />
      )}
    </Box>
  );
};

export default CreateEditUserModal;
